/* eslint-disable no-unused-vars */

import _ from 'lodash';
import { useSelector } from 'react-redux';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useLocation, useSearchParams, useNavigate, useParams } from 'react-router-dom';

import SearchField from '@components/search-field';
import FilterHeader from '@components/filters/filter-header';
import TaskBookmark from '../task-bookmark';
import TasksFilterGroups from '@components/filters/task-filter-group';

import { mapValues, countFilters, tasksDefaultFilterValues, statusTabs } from '../helper';
import { checkValueInObjectIsNotEmpty } from '@utilities/helper.util';
import { filterActions } from '@redux/slices/filters';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { getAppDispatch } from '@utilities/dispatch.util';
import { useToastContext } from '@context/toaster.context';
import BookmarksService from '@services/bookmarks.service';
import { bookmarksActions } from '@redux/slices/bookmarks';

import SiteFilterGroup from '@components/filters/site-filter-group';
import ResetAndViewButton from '@components/filters/reset-view-button';
import style from '../style.module.scss';
import dynamicFilterList, { getCountOnEachFilterObject } from '@utilities/dynamic-filter-list';
import DynamicFilterContainer from '@components/dynamic-filter-container';
import { hasFilterChanged } from '@utilities/filter-is-changed';
import SaveModal from '@components/modal/variants/save-modal';

const TaskFilter = (
  {
    length,
    filtersCount,
    dynamicFilterCountOnEachField,
    setFilterActive,
    noGap,
    onSearch,
    resetFilters,
    setResetFilters,
    isFilterActive,
    setFiltersCount,
    onChangeFilters,
    sitesSiteTypeDataMap,
    sitesSiteStatusDataMap,
    sitesCheckPriorityDataMap,
    assignedUserGroups,
    sitesWorkOrderPriorityDataMap,
    sitesAreaHierarchyTreeDataMap,
    tasks = [],
    selectedSort,
    selectTodoOrInProgressStatus,
    asset = null,
    filterType = 'all',
    tab,
    equipmentsEquipmentCategoryDataMap = {},
    equipmentsEquipmentTypeDataMap = {}
  },
  ref
) => {
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = getAppDispatch();
  const { width } = useWindowDimensions();
  const { ToasterUtil } = useToastContext();

  const bookmarkFilter = searchParams.get('bookmarkFilter');
  const dueDateFilter = searchParams.get('dueDate');
  const downloadFilter = searchParams.get('download');
  const bookmarkId = searchParams.get('bookmarkId');
  const bookmarkName = searchParams.get('bookmarkName');
  const runs = searchParams.get('runs') || '';
  const templates = searchParams.get('templates') || '';
  const from = searchParams.get('from');
  const { id } = useParams();

  const { unit, coordinates } = useSelector((state) => state.cache);
  const tasksFilter = useSelector((state) => state?.filters?.tasksFilter) || {};
  const {
    picklistsMap = {},
    areaHierarchyTreeDataMap = {},
    areaHierarchyDataMap
  } = useSelector((state) => state?.picklists);

  const [inputValue, setInputValue] = useState('');
  const [filters, setFilters] = useState(tasksDefaultFilterValues);

  const [initialY, setInitialY] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [updateBookmarkModalVisible, setUpdateBookmarkModalVisible] = useState(false);

  const {
    siteTypeTags,
    siteStatusTags,
    assignableUsersTags,
    checkPriorityTags,
    workOrderPriorityTags,
    treeData,
    taskTypeTag,
    taskStatusTags,
    taskRecurringTemplateTags,
    workOrderTypeTags,
    workOrderTypeTagMap,
    inspectionTypeTags,
    inspectionRunTags,
    hseEventsCategoriesTags,
    checkTypeTags,
    checkRecurrancePriorityTags,
    checkTypeTagsMap,
    inspectionTypeTagsMap,
    equipmentCategoryTags,
    equipmentTypeTags,
    inspectionTypeFrequency
  } = React.useMemo(() => {
    return mapValues({
      sitesSiteTypeDataMap,
      sitesSiteStatusDataMap,
      sitesCheckPriorityDataMap,
      assignedUserGroups,
      sitesWorkOrderPriorityDataMap,
      sitesAreaHierarchyTreeDataMap,
      tasks,
      filterType,
      equipmentsEquipmentCategoryDataMap,
      equipmentsEquipmentTypeDataMap
    });
  }, [
    sitesSiteTypeDataMap,
    sitesSiteStatusDataMap,
    sitesCheckPriorityDataMap,
    assignedUserGroups,
    sitesWorkOrderPriorityDataMap,
    sitesAreaHierarchyTreeDataMap,
    equipmentsEquipmentCategoryDataMap,
    equipmentsEquipmentTypeDataMap
  ]);

  useEffect(() => {
    if (selectTodoOrInProgressStatus) {
      setFilters({ ...tasksDefaultFilterValues, taskStatus: { value: ['To do', 'In progress'] } });
    } else {
      if (!_.isEmpty(bookmarkFilter)) {
        const filtersInDecode = decodeURIComponent(bookmarkFilter);
        const filtersInParse = JSON.parse(filtersInDecode);
        // if (filtersInParse?.filters) {
        //   // check if user save old filter of inspection type
        //   const inspectionTypeFilter = filtersInParse?.filters?.inspectionType?.value || [];
        //   if (inspectionTypeFilter?.length > 0 && !_.isEmpty(inspectionTypeFrequency)) {
        //     let selectedValues = inspectionTypeFilter?.map((x) => {
        //       return inspectionTypeFrequency[x];
        //     });
        //     selectedValues = _.flatten(selectedValues);
        //     if (selectedValues?.length > 0) {
        //       setFilters({
        //         ...filtersInParse?.filters,
        //         inspectionType: {
        //           value: _.flatten(selectedValues)
        //         }
        //       });
        //     } else {
        //       setFilters(filtersInParse?.filters);
        //     }
        //   } else {
        //     setFilters(filtersInParse?.filters);
        //   }
        // }
        setFilters(filtersInParse?.filters);
        filtersInParse?.filters &&
          filtersInParse?.filters['searched'] &&
          setInputValue(filtersInParse?.filters['searched'] || '');
      } else if (checkValueInObjectIsNotEmpty(tasksFilter)) {
        setFilters(tasksFilter);
        onChangeFilters?.(tasksFilter);
        setInputValue(tasksFilter?.searched || '');
        onSearch(tasksFilter?.searched || '');
      } else {
        setInputValue('');
        setFilters(tasksDefaultFilterValues);
      }
    }
    if (resetFilters) resetBtnEvent({ filterActive: width < 768 ? false : true });
    return () => {};
  }, [resetFilters, state?.resetFilters, bookmarkFilter, inspectionTypeFrequency]);

  const isFilterChangedInBookmark = useMemo(() => {
    if (!_.isEmpty(bookmarkFilter)) {
      const filtersInDecode = decodeURIComponent(bookmarkFilter);
      const oldFilter = JSON.parse(filtersInDecode);
      const oldFilterSearch = oldFilter?.filters['searched'] || '';

      const result =
        oldFilterSearch !== inputValue
          ? true
          : hasFilterChanged({
              oldFilter: oldFilter?.filters,
              newFilter: { ...filters }
            });

      return result;
    } else return false;
  }, [bookmarkFilter, filters, inputValue]);

  useEffect(() => {
    if (downloadFilter) updateFilters('download', 'selected', downloadFilter);
    if (dueDateFilter === 'today') {
      updateFilters('dueDate', 'value', moment());
      updateFilters('dueDate', 'selected', 'on');
    } else if (dueDateFilter === 'past') {
      updateFilters('dueDate', 'selected', 'past');
      updateFilters('dueDate', 'value', ['Unlimited']);
    }
  }, [downloadFilter, dueDateFilter]);

  useEffect(() => {
    const filterCount = countFilters(filters, inputValue) || 0;
    setFiltersCount(filterCount);
    dispatch && dispatch?.(filterActions.setTaskFiltersCount(filterCount));
  }, [filters, inputValue, dispatch]);

  useEffect(() => {
    if (isFilterActive) document.body.classList.add('filter-open');
    else document.body.classList.remove('filter-open');
  }, [isFilterActive]);

  useEffect(() => {
    return () => {
      if (isFilterActive) {
        setFilterActive(false);
        document.body.classList.remove('filter-open');
      }
    };
  }, [isFilterActive]);

  const startTouch = (e) => setInitialY(e.touches[0].clientY);

  const moveTouch = (e) => {
    let currentY = e.touches[0].clientY;
    let diffY = initialY - currentY;
    if (diffY < 0) setFilterActive(false);
    setInitialY(e.touches[0].clientY);
  };

  const resetBtnEvent = ({ filterActive = true }) => {
    setResetFilters((pre) => !pre);
    navigate(`${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`, {
      replace: true
    });
    setFilterActive(filterActive);
    setInputValue('');
    setFilters(tasksDefaultFilterValues);
    onChangeFilters?.(tasksDefaultFilterValues);
    onSearch('');
    dispatch?.(filterActions.setTaskFilters({ ...tasksDefaultFilterValues, searched: '' }));
  };

  const leftBtnEvent = () => setFilterActive(false);

  const handleUserInput = (e) => {
    const valueSearched = (e && e.target.value) || '';
    onSearch?.(valueSearched);
    dispatch?.(filterActions.setTaskFilters({ ...filters, searched: valueSearched }));
    setInputValue(valueSearched);
  };

  const handleKeyDown = (e) => {
    if (width < 768 && e.key === 'Enter') setFilterActive(false);
  };
  const updateFilters = (name, key, value) => {
    const filtersCopy = { ...filters };
    let newFilter = {
      ...filtersCopy,
      [name]: {
        ...Object.assign({}, filtersCopy[name]),
        [key]: value,
        ...(key === 'selected' && value === 'on' && { value: '' }),
        ...(key === 'selected' && (value === 'past' || value === 'next') && { value: [] }),
        ...(key === 'selected' && value === 'range' && { value: ['', ''] }),
        ...(key === 'selected' && value === 'last' && { value: ['last'] })
      }
    };
    setFilters({ ...newFilter });
    onChangeFilters?.({ ...newFilter });
    dispatch?.(filterActions.setTaskFilters({ ...newFilter, searched: inputValue }));
  };

  React.useImperativeHandle(ref, () => ({
    clearFilterOnTaskFilterPage() {
      setFilters(tasksDefaultFilterValues);
    }
  }));

  useEffect(() => {
    if (asset) {
      setInputValue('');
      setFilters(tasksDefaultFilterValues);
    }
  }, [asset]);

  const setUpdateBookmarkEvent = () => {
    const changeFilters =
      filterType === 'all'
        ? filters
        : {
            ...filters,
            taskType: {
              value:
                filterType === 'all'
                  ? filters?.taskType?.value?.length > 0
                    ? filters?.taskType?.value
                    : ['Inspections', 'Work orders', 'Work requests', 'HSE events', 'Checks']
                  : filterType === 'inspection' || filterType === 'Inspection'
                  ? ['Inspections']
                  : filterType === 'workOrder' || filterType === 'Work Order'
                  ? ['Work orders']
                  : filterType === 'workRequest' || filterType === 'Work Request'
                  ? ['Work requests']
                  : filterType === 'hseEvents'
                  ? ['HSE event']
                  : filterType === 'Check' || filterType === 'check'
                  ? ['Checks']
                  : filters?.taskType?.value || []
            },
            inspectionRun: {
              value:
                !_.isEmpty(runs) && inspectionRunTags?.length > 0
                  ? inspectionRunTags
                  : filters?.inspectionRun?.value || []
            },
            recurringTemplate: {
              value:
                !_.isEmpty(templates) && taskRecurringTemplateTags?.length > 0
                  ? taskRecurringTemplateTags
                  : filters?.recurringTemplate?.value || []
            }
          };

    const filtersString = {
      filters: {
        ...changeFilters,
        searched: inputValue
      },
      sort: selectedSort,
      [`${from}Id`]: id,
      tab: Object.keys(statusTabs)[parseInt(tab)]
    };

    const taskBookmarkData = {
      filters: JSON.stringify(filtersString),
      bookmarkId
    };

    ToasterUtil.showSuccess(`Bookmark changes saved.`);
    BookmarksService.updateBookmarkData({ bookmarkId, updatedData: taskBookmarkData });
    dispatch?.(bookmarksActions.setBookmarkValidity(true));

    BookmarksService.updateBookmarksNameAndAtAGlance({
      name: 'Tasks',
      atAGlance: true,
      bookmarkId,
      updatedData: taskBookmarkData
    });

    const params = new URLSearchParams(searchParams);
    params.set('bookmarkFilter', JSON.stringify(filtersString));
    setSearchParams(params, { replace: true });
    setUpdateBookmarkModalVisible(false);
  };

  const tasksTitle = useMemo(() => {
    return filterType?.toLocaleLowerCase() === 'inspection'
      ? 'Inspections'
      : filterType === 'workOrder' || filterType === 'Work Order'
      ? 'Work orders'
      : filterType === 'workRequest' || filterType === 'Work Request'
      ? 'Work Requests'
      : filterType?.toLocaleLowerCase() === 'check'
      ? 'Checks'
      : filterType === 'hseEvents' || filterType === 'HSE'
      ? 'HSE Events'
      : filterType === 'newOrEditAssets'
      ? 'New or edited assets'
      : 'Tasks';
  }, [filterType]);

  const showSiteFilterGroupOnTasksListingPage = useMemo(() => {
    return (
      (filterType !== 'Inspection' || filterType !== 'Work Order') &&
      (_.isEmpty(asset) ||
        filterType === 'workRequest' ||
        filterType === 'Work Request' ||
        filterType === 'newOrEditAssets' ||
        filterType?.toLocaleLowerCase() === 'check')
    );
  }, [filterType, asset]);

  const filterConfigs = useMemo(() => {
    const filterConfigs = dynamicFilterList({
      type: 'tasks-listing',
      updateFilters,
      areaHierarchyDataMap,
      filters,
      picklistsMap,
      taskStatusTags,
      taskTypeTag,
      assignableUsersTags,
      taskRecurringTemplateTags,
      inspectionRunTags,
      inspectionTypeTagsMap,
      checkTypeTagsMap,
      hseEventsCategoriesTags,
      workOrderTypeTagMap,
      equipmentCategoryTags,
      equipmentTypeTags
    });

    return filterConfigs;
  }, [
    filters,
    areaHierarchyDataMap,
    filters,
    picklistsMap,
    taskStatusTags,
    taskTypeTag,
    assignableUsersTags,
    taskRecurringTemplateTags,
    inspectionRunTags,
    inspectionTypeTagsMap,
    checkTypeTagsMap,
    hseEventsCategoriesTags,
    workOrderTypeTagMap,
    equipmentCategoryTags,
    equipmentTypeTags
  ]);

  return (
    <>
      <div
        className={`op-sidebarfilter ${isFilterActive ? 'open' : ''} ${noGap ? '' : 'gap'}`}
        style={{
          padding: isFilterActive ? '10px 0px' : '10px 0px'
        }}
      >
        <div
          onTouchStart={startTouch}
          onTouchMove={moveTouch}
          className={`${isFilterActive ? style.paddingLeftAndRight : style.isInActivePadding}`}
        >
          <FilterHeader
            title={'Filters'}
            filterCount={filtersCount || 0}
            resetBtnEvent={() => resetBtnEvent({})}
            leftBtnEvent={leftBtnEvent}
            openBookmarkModalEvent={() => {
              if (bookmarkId || isFilterChangedInBookmark) {
                if (isFilterChangedInBookmark) setUpdateBookmarkModalVisible(true);
              } else {
                setIsModalVisible(true);
              }
            }}
            bookmarkId={bookmarkId ? true : false}
            isFilterChangedInBookmark={isFilterChangedInBookmark}
            width={width}
            detailPage={asset}
          />
        </div>
        <div
          className={`op-filter-search ${
            isFilterActive ? style.paddingLeftAndRight : style.isInActivePadding
          } `}
          style={{ marginTop: '25px', marginBottom: '16px' }}
        >
          <SearchField
            placeholder="Search"
            value={inputValue}
            onKeyDown={handleKeyDown}
            onChange={handleUserInput}
            isClearable
          />

          <DynamicFilterContainer
            filterConfigs={filterConfigs}
            filters={filters}
            unit={unit}
            filterCount={dynamicFilterCountOnEachField || 0}
            resetBtnEvent={() => resetBtnEvent({})}
          />
        </div>
        {/* <p
          className="label-regular op-filter-label"
          style={{ padding: '29px 0px 32px 0px', marginBottom: '0px' }}
        >
          {tasksTitle}
        </p> */}

        <div className={style.filterHeader}>
          <p>{tasksTitle}</p>
        </div>

        <TasksFilterGroups
          filterType={filterType}
          taskTypeTag={taskTypeTag}
          filters={filters}
          updateFilters={updateFilters}
          taskStatusTags={taskStatusTags}
          asset={asset}
          workOrderTypeTags={workOrderTypeTags}
          workOrderTypeTagMap={workOrderTypeTagMap}
          assignableUsersTags={assignableUsersTags}
          workOrderPriorityTags={workOrderPriorityTags}
          isActive={isFilterActive}
          picklistsMap={picklistsMap}
          taskRecurringTemplateTags={taskRecurringTemplateTags}
          inspectionRunTags={inspectionRunTags}
          inspectionTypeTags={inspectionTypeTags}
          hseEventsCategoriesTags={hseEventsCategoriesTags}
          checkTypeTags={checkTypeTags}
          checkTypeTagsMap={checkTypeTagsMap}
          inspectionTypeTagsMap={inspectionTypeTagsMap}
          checkRecurrancePriorityTags={checkRecurrancePriorityTags}
          tasksListing
          workOrderTypeTagsMap={workOrderTypeTagMap}
          equipmentCategoryTags={equipmentCategoryTags}
          equipmentTypeTags={equipmentTypeTags}
        />

        {showSiteFilterGroupOnTasksListingPage && (
          <SiteFilterGroup
            coordinates={coordinates}
            filters={filters}
            updateFilters={updateFilters}
            isActive={isFilterActive}
            treeData={treeData}
            areaHierarchyTreeDataMap={areaHierarchyTreeDataMap}
            siteTypeTags={siteTypeTags}
            picklistsMap={picklistsMap}
            siteStatusTags={siteStatusTags}
            unit={unit}
            tasksListing
            filterType={filterType}
          />
        )}

        {/* {!asset && ( */}
        <ResetAndViewButton
          clickResetButton={() => resetBtnEvent({ filterActive: false })}
          length={length}
          setFilterActiveEvent={(value) => setFilterActive(value)}
          setIsModalVisibleEvent={(value) => setIsModalVisible(value)}
          width={width}
          updateBookmarkId={bookmarkId}
          bookmarkName={bookmarkName}
          setUpdateBookmarkEvent={() => {
            setUpdateBookmarkModalVisible(true);
          }}
          detailPage={asset}
        />
        {/* )} */}
      </div>
      {isModalVisible && (
        <TaskBookmark
          open={isModalVisible}
          setOpen={(value) => setIsModalVisible(value)}
          selectedSort={selectedSort}
          filters={{ ...filters, searched: inputValue }}
          filterType={filterType}
          asset={asset}
          inspectionRunTags={inspectionRunTags}
          taskRecurringTemplateTags={taskRecurringTemplateTags}
          tab={tab}
        />
      )}

      {updateBookmarkModalVisible && (
        <SaveModal
          open={updateBookmarkModalVisible}
          setOpen={(value) => {
            setUpdateBookmarkModalVisible(value);
          }}
          bookmarkSaveChanges={true}
          onSave={setUpdateBookmarkEvent}
        />
      )}
    </>
  );
};

export default React.memo(forwardRef(TaskFilter), _.isEqual);
